/* eslint-disable complexity */
import { ALIGN_ITEMS, FLEX_CONTAINER, FLEX_CONTAINER_ROW, FLEX_ITEM_AUTO, H_100, PADDING, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { createHtmlElement } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { DATA_SOURCE_SERVICE, LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { Injectable } from "@cervval.js/common/src/dependencies/injectable";
import { injector } from "@cervval.js/common/src/dependencies/injector";
import { DataSource, DataSourceItem } from "@cervval.js/common/src/forms/dataSource";
import { PropertyConfig, PropertyOptionsCheckboxGroup, PropertyOptionsDuration, PropertyOptionsList, PropertyOptionsMinutesIntervals, PropertyOptionsRange, PropertyOptionsTextArea, PropertyOptionsTime } from "@cervval.js/common/src/forms/propertyConfig";
import { PropertyConfigType } from "@cervval.js/common/src/forms/propertyConfig.types";
import { DefaultDataSourceService } from "@cervval.js/common/src/services/dataSourceService.impl/defaultDataSourceService";
import { requiredValidatorName } from "@cervval.js/common/src/services/validation.impl/validators/required.validator";
import { ValueOrFunction, getVOFValue } from "@cervval.js/common/src/tools/object.tools";
import { filterString } from "@cervval.js/common/src/tools/string.tools";
import { anyReallyAny } from "@cervval.js/common/tools";
import { CheckboxMode } from "../components/checkbox/checkbox";
import { CjsEllipsis } from "../components/ellipsis/ellipsis";
import { AcceptInputValueType, CjsInput } from "../components/inputs/input";
import { CjsInputWithButton } from "../components/inputs/inputWithButton/inputWithButton";
import { SMART_SELECT_ITEM_ELEMENT } from "../components/smartSelect/smartSelect";
import { CjsCheckboxEditor } from "./checkboxEditor/checkboxEditor";
import { CjsCheckboxGroupEditor } from "./checkboxGroupEditor/checkboxGroupEditor";
import { CjsEditableTextEditor } from "./editableTextEditor/editableTextEditor";
import { CjsEditor } from "./editor";
import { EDITOR_FACTORY_SERVICE, EditorBuilder, EditorFactoryService, NewEditorOptions } from "./editorFactory.service";
import { CjsColorEditor } from "./editorsWithPicker/colorEditor/colorEditor";
import { CjsDateEditor } from "./editorsWithPicker/dateEditor/dateEditor";
import { CjsDateIntervalCalendarEditor } from "./editorsWithPicker/dateIntervalCalendarEditor/dateIntervalCalendarEditor";
import { CjsDateTimeEditor } from "./editorsWithPicker/dateTimeEditor/dateTimeEditor";
import { CjsDurationEditor } from "./editorsWithPicker/durationEditor/durationEditor";
import { CjsImageEditor } from "./editorsWithPicker/imageEditor/imageEditor";
import { CjsTimeEditor } from "./editorsWithPicker/timeEditor/timeEditor";
import { CjsFileListEditor } from "./fileListEditor/fileListEditor";
import { CjsInputNumberEditor } from "./inputNumberEditor/inputNumberEditor";
import { CjsInputRangeEditor } from "./inputRangeEditor/inputRangeEditor";
import { CjsInputSearchEditor } from "./inputSearchEditor/inputSearchEditor";
import { CjsInputTextEditor } from "./inputTextEditor/inputTextEditor";
import { CjsMinutesIntervalsStringEditor } from "./minutesIntervalsEditor/minutesIntervalsEditor";
import { CjsPhoneNumberEditor } from "./phoneNumberEditor/phoneNumberEditor";
import { CjsReferenceListEditor } from "./referenceListEditor/referenceListEditor";
import { CjsReferenceTableEditor } from "./referenceTableEditor/referenceTableEditor";
import { CjsSelectEditor } from "./selectEditor/selectEditor";
import { CjsTinyMceEditor } from "./tinyMceEditor/tinyMceEditor";

@Injectable(EDITOR_FACTORY_SERVICE)
export class DefaultEditorFactoryService implements EditorFactoryService {

    protected _customTypes = new Map<string, EditorBuilder>();

    /** @inheritdoc */
    public registerEditorType(
        typeUid: string,
        params: {
            newEditor: EditorBuilder
        }
    ): void {
        this._customTypes.set(typeUid, params.newEditor);
    }

    /** @inheritdoc */
    public newEditor(pc: PropertyConfig, options?: NewEditorOptions): CjsEditor<unknown> {

        // if a custom builder is registered for this type, use it
        const customBuilder = this._customTypes.get(pc.typeUid);
        if (customBuilder) {
            return customBuilder(pc, options);
        }

        let editor: CjsEditor<unknown>;
        switch (pc.typeUid) {
            case PropertyConfigType.STRING:
            case PropertyConfigType.COORDINATE:
            case PropertyConfigType.UID: {
                editor = initTextEditor(pc);
                break;
            }
            case PropertyConfigType.TEXTAREA:
                editor = initTextareaEditor(pc);
                break;
            case PropertyConfigType.ID:
            case PropertyConfigType.NUMBER:
            case PropertyConfigType.AGE: {
                editor = initInputNumberEditor(pc);
                break;
            }
            case PropertyConfigType.SEARCH:
                editor = initInputSearchEditor(<PropertyConfig<PropertyConfigType, PropertyOptionsList>>pc, options?.dataSourceContext);
                break;
            case PropertyConfigType.DATE:
                editor = initDateEditor(pc);
                break;
            case PropertyConfigType.FINISH_DATE:
                editor = initFinishDateEditor(pc);
                break;
            case PropertyConfigType.COLOR:
                editor = initColorEditor(pc);
                break;
            case PropertyConfigType.LISTSELECT:
                editor = initSelectListEditor(<PropertyConfig<PropertyConfigType, PropertyOptionsList>>pc, options);
                break;
            case PropertyConfigType.REFLIST:
                editor = initRefListEditor(<PropertyConfig<PropertyConfigType, PropertyOptionsList>>pc, options?.dataSourceContext);
                break;
            case PropertyConfigType.REFLIST_ORDERED:
                editor = initRefListEditor(<PropertyConfig<PropertyConfigType, PropertyOptionsList>>pc, options?.dataSourceContext, { ordered: true });
                break;
            case PropertyConfigType.REFTABLE:
                editor = initRefTableEditor(<PropertyConfig<PropertyConfigType, PropertyOptionsList>>pc, options?.dataSourceContext);
                break;
            case PropertyConfigType.TINYMCE:
                editor = initTinyMceEditor();
                break;
            case PropertyConfigType.CHECKBOX:
                editor = new CjsCheckboxEditor();
                break;
            case PropertyConfigType.CHECKBOX_GROUP:
                editor = initCheckboxGroupEditor(pc as PropertyConfig<PropertyConfigType, PropertyOptionsCheckboxGroup>);
                break;
            case PropertyConfigType.SWITCH:
                editor = new CjsCheckboxEditor(undefined, { mode: CheckboxMode.SWITCH });
                break;
            case PropertyConfigType.FILELIST: {
                editor = new CjsFileListEditor();

                // Limit editor size
                editor.style.maxHeight = "250px";
            }
                break;
            case PropertyConfigType.DURATION:
                editor = initDurationEditor(<PropertyConfig<PropertyConfigType, PropertyOptionsDuration>>pc);
                break;
            case PropertyConfigType.DATETIME:
                editor = initDateTimeEditor(pc);
                break;
            case PropertyConfigType.EDITABLE_TEXT:
                editor = initEditableTextEditor(pc);
                break;
            case PropertyConfigType.TIME:
                editor = initTimeEditor(<PropertyConfig<PropertyConfigType, PropertyOptionsTime>>pc);
                break;
            case PropertyConfigType.TIME_IN_SECONDS:
                editor = initTimeInSecondsEditor(<PropertyConfig<PropertyConfigType, PropertyOptionsTime>>pc);
                break;
            case PropertyConfigType.DATE_INTERVAL:
                editor = initDateIntervalCalendarEditor(pc);
                break;
            case PropertyConfigType.PHONE_NUMBER:
                editor = initPhoneNumberEditor(pc);
                break;
            case PropertyConfigType.RANGE:
                editor = initRangeEditor(<PropertyConfig<PropertyConfigType, PropertyOptionsRange>>pc);
                break;
            case PropertyConfigType.IMAGE:
                editor = initImageEditor(pc);
                break;
            case PropertyConfigType.MINUTES_INTERVALS:
                editor = initMinutesIntervalsEditor(pc);
                break;
            default: {
                editor = initTextEditor(pc);
                break;
            }
        }
        return editor;
    }
}


/**
 * Create a default InputTextEditor text input
 */
function initTextEditor(propertyConfig: PropertyConfig): CjsInputTextEditor {
    const editor = new CjsInputTextEditor();
    setInputWithButtonCommonOptions(editor, propertyConfig);
    return editor;
}

/**
 * Create a default EditableTextEditor text input
 */
function initEditableTextEditor(propertyConfig: PropertyConfig): CjsEditableTextEditor<string> {
    const editor = new CjsEditableTextEditor();
    if (propertyConfig.options?.placeholder) {
        editor.options.setWithoutRefresh({ placeholder: propertyConfig.options?.placeholder });
    }
    return editor;
}

/**
 * Create a default MinutesIntervalsEditor
 */
function initMinutesIntervalsEditor(propertyConfig: PropertyConfig): CjsMinutesIntervalsStringEditor {
    const editor = new CjsMinutesIntervalsStringEditor();
    setInputWithButtonCommonOptions(editor, propertyConfig);
    const options = (propertyConfig.options as PropertyOptionsMinutesIntervals);
    if (options?.dayFormat) {
        editor.options.setWithoutRefresh({ dayFormat: options.dayFormat });
    }
    return editor;
}

/**
 * Create a default InputNumberEditor numbers input (<type="number">)
 */
function initInputNumberEditor(propertyConfig: PropertyConfig): CjsInputNumberEditor {
    const editor = new CjsInputNumberEditor();
    setInputWithButtonCommonOptions(editor, propertyConfig);
    return editor;
}

/**
 * Create a default ColorEditor
 */
function initColorEditor(propertyConfig: PropertyConfig): CjsColorEditor {
    const editor = new CjsColorEditor();
    setInputWithButtonCommonOptions(editor, propertyConfig);
    return editor;
}

/**
 * Create a default DateEditor
 */
function initDateEditor(propertyConfig: PropertyConfig): CjsDateEditor {
    const editor = new CjsDateEditor();
    setInputWithButtonCommonOptions(editor, propertyConfig);
    return editor;
}

/**
 * Create a default DateEditor for finish date
 */
function initFinishDateEditor(propertyConfig: PropertyConfig): CjsDateEditor {
    const editor = initDateEditor(propertyConfig);
    editor.options.setWithoutRefresh({ isFinishDate: true });
    return editor;
}

/**
 * Create a default DateTimeEditor
 */
function initDateTimeEditor(propertyConfig: PropertyConfig): CjsDateTimeEditor {
    const editor = new CjsDateTimeEditor();
    setInputWithButtonCommonOptions(editor, propertyConfig);
    return editor;
}

/**
 * Create a default durationEditor
 */
function initDurationEditor(propertyConfig: PropertyConfig<PropertyConfigType, PropertyOptionsDuration>): CjsDurationEditor {
    const editor = new CjsDurationEditor(undefined, propertyConfig.options?.pickerOptions);
    setInputWithButtonCommonOptions(editor, propertyConfig);
    return editor;
}

/**
 * Create a default timeEditor
 */
function initTimeEditor(propertyConfig: PropertyConfig<PropertyConfigType, PropertyOptionsTime>): CjsTimeEditor {
    const editor = new CjsTimeEditor(undefined, propertyConfig.options?.pickerOptions);
    setInputWithButtonCommonOptions(editor, propertyConfig);
    return editor;
}

/**
 * Create a default timeEditor
 */
function initTimeInSecondsEditor(propertyConfig: PropertyConfig<PropertyConfigType, PropertyOptionsTime>): CjsTimeEditor {
    const editor = initTimeEditor(propertyConfig);
    editor.pickerOptions.setWithoutRefresh({ useSeconds: true });
    return editor;
}

/**
 * Create a default DateIntervalEditor
 */
function initDateIntervalCalendarEditor(propertyConfig: PropertyConfig): CjsDateIntervalCalendarEditor {
    const editor = new CjsDateIntervalCalendarEditor();
    setInputWithButtonCommonOptions(editor, propertyConfig);
    setInputWithButtonCommonOptions(editor, propertyConfig, editor.finishDateInput);
    return editor;
}

/**
 * Create a default SelectListEditor with a selectEditor
 */
export function initSelectListEditor<T extends { id: AcceptInputValueType, name: string }>(propertyConfig: PropertyConfig<PropertyConfigType, PropertyOptionsList>, options?: NewEditorOptions): CjsSelectEditor<DataSourceItem<T>, AcceptInputValueType> {

    const dataSource: DataSource<T, AcceptInputValueType, unknown> = initDataSource(<PropertyConfig<PropertyConfigType, PropertyOptionsList>>propertyConfig);

    const getItemValue = (item: DataSourceItem<T> | undefined): AcceptInputValueType | undefined => { return item ? dataSource.getValueFromItem(item) : undefined; };
    const getItemByValue = (items: DataSourceItem<T>[], id: AcceptInputValueType): DataSourceItem<T> | undefined => dataSource.getItemFromValue(id);
    const getItemDisplayString = (item: DataSourceItem<T>): string => {
        const value = getItemValue(item);
        return value != null ? dataSource.getDisplayString(value, item) : "";
    };
    const getData = (): DataSourceItem<T>[] => dataSource.getItems(options?.dataSourceContext);

    let firstAndOnlyItemId = undefined;
    if (options?.isCreationForm && Boolean(propertyConfig.validators?.[requiredValidatorName])) {
        const data = getData();
        if (data.length === 1) {
            firstAndOnlyItemId = getItemValue(data[0]);
        }
    }

    const editor = new CjsSelectEditor<DataSourceItem<T>, AcceptInputValueType>({
        smartSelectOptions: {
            data: getData,
            selectedValue: firstAndOnlyItemId,
            placeholderText: propertyConfig.options?.placeholder,
            multiSelect: {
                enable: Boolean(getVOFValue(propertyConfig.options?.multiSelect?.enable)),
                multiSelectionItemFormatter: (selectedItems) => {
                    const ellipsis = new CjsEllipsis(selectedItems.map(i => getItemDisplayString(i)).join(", "));
                    ellipsis.classList.add(FLEX_CONTAINER, ALIGN_ITEMS.CENTER, H_100, W_100);

                    return createHtmlElement("div", {
                        cssClass: [FLEX_CONTAINER_ROW, FLEX_ITEM_AUTO, ALIGN_ITEMS.CENTER, W_100, H_100],
                        innerHTML: ellipsis
                    });
                },
                selectAllOnInit: propertyConfig.options?.multiSelect?.selectAllOnInit
            },
            callbacksToOverride: {
                getItemValue,
                getItemByValue
            },
            itemFormatter: (item) => {
                const itemDiv = createHtmlElement("div", {
                    innerHTML: createHtmlElement("span", { innerHTML: getItemDisplayString(item) }),
                    cssClass: [SMART_SELECT_ITEM_ELEMENT, FLEX_CONTAINER_ROW, FLEX_ITEM_AUTO, H_100, ALIGN_ITEMS.CENTER],
                    style: {
                        paddingLeft: "5px"
                    },
                    attributes: {
                        "value": String(getItemValue(item))
                    }
                });

                if (!editor.disabled) {
                    itemDiv.style.cursor = "pointer";
                }
                return itemDiv;
            },
            emptyValue: propertyConfig.options?.emptyValue,
            filterDisabled: Boolean(propertyConfig.options?.filterDisabled),
            filterComparator: (p, renderCacheData) => {
                if (propertyConfig.options?.filterComparator) { return propertyConfig.options.filterComparator(p, renderCacheData); }
                else {
                    return p.item != null && filterString(getItemDisplayString(p.item), p.filterValue);
                }
            }
        }
    });

    return editor;
}

/** Init input search editor */
function initInputSearchEditor<T extends { id: AcceptInputValueType, name: string }>(propertyConfig: PropertyConfig<PropertyConfigType, PropertyOptionsList>, context?: anyReallyAny): CjsInputSearchEditor<DataSourceItem<T>, AcceptInputValueType> {

    const dataSource: DataSource<T, AcceptInputValueType, unknown> = initDataSource(<PropertyConfig<PropertyConfigType, PropertyOptionsList>>propertyConfig);

    const getItemValue = (item: DataSourceItem<T> | undefined): AcceptInputValueType | undefined => (item ? dataSource.getValueFromItem(item) : undefined);
    const getItemByValue = (items: DataSourceItem<T>[], id: AcceptInputValueType): DataSourceItem<T> | undefined => dataSource.getItemFromValue(id);
    const getItemDisplayString = (item: DataSourceItem<T>): string => {
        const value = getItemValue(item);
        return value != null ? dataSource.getDisplayString(value, item) : "";
    };
    const getData = (): DataSourceItem<T>[] => dataSource.getItems(context);

    const editor = new CjsInputSearchEditor<DataSourceItem<T>, AcceptInputValueType>({ disabled: propertyConfig.options?.disabled }, {
        inputSearch: {
            formatValueDisplay: item => getItemDisplayString(item)
        },
        smartSelect: {
            data: getData,
            placeholderText: propertyConfig.options?.placeholder,
            callbacksToOverride: {
                getItemValue,
                getItemByValue
            },
            filterDisabled: true,
            filterComparator: (p) => p.item != null && filterString(getItemDisplayString(p.item), p.filterValue, { startsWith: true }),
            itemFormatter: (item) => {
                const itemDiv = createHtmlElement("div", {
                    innerHTML: createHtmlElement("span", { innerHTML: getItemDisplayString(item) }),
                    cssClass: [SMART_SELECT_ITEM_ELEMENT, FLEX_CONTAINER_ROW, FLEX_ITEM_AUTO, H_100, ALIGN_ITEMS.CENTER],
                    style: {
                        paddingLeft: "5px"
                    },
                    attributes: {
                        "value": String(getItemValue(item))
                    }
                });
                return itemDiv;
            },
            emptyValue: propertyConfig.options?.emptyValue
        }
    });

    // Set input options
    setInputWithButtonCommonOptions(editor, propertyConfig);
    return editor;
}

/** Init checkbox group editor */
function initCheckboxGroupEditor(propertyConfig: PropertyConfig<PropertyConfigType, PropertyOptionsCheckboxGroup>): CjsCheckboxGroupEditor {
    const dataSource: DataSource<anyReallyAny, string | number, unknown> = initDataSource(<PropertyConfig<PropertyConfigType, PropertyOptionsList>>propertyConfig);

    const editor = new CjsCheckboxGroupEditor(
        {
            disabled: propertyConfig.options?.disabled
        },
        {
            checkboxOptions: dataSource.getItems().map(i => {
                const itemValue = dataSource.getValueFromItem(i);
                return {
                    ...i.item,
                    uid: itemValue,
                    label: itemValue ? dataSource.getDisplayString(itemValue) : ""
                };
            })
        }
    );

    return editor;
}

/**
 * ini
 * @param propertyConfig 
 * @returns 
 */
function initDataSource<T extends { id: string | number, name: string }>(propertyConfig: PropertyConfig<PropertyConfigType, PropertyOptionsList>): DataSource<T, string | number, unknown> {
    // Handle Data Source in itemList property config options
    if (propertyConfig.options && typeof propertyConfig.options.itemList === "string") {
        // Retrieve the data source by its UID using the dataSourceService
        const dataSourceUID = propertyConfig.options.itemList;
        const dataSourceService = injector.get<DefaultDataSourceService>(DATA_SOURCE_SERVICE);
        const dataSourceFromService = dataSourceService.getDataSource<T, string | number>(dataSourceUID);

        // Set the new get functions to correspond to the data source object
        if (!dataSourceFromService) {
            injector.get<LogService>(LOG_SERVICE).error("Datasource '" + dataSourceUID + "' not found for propertyConfig " + propertyConfig.propertyName);
        } else {
            return dataSourceFromService;
        }
    }

    // By default, a data source is built using the itemList 
    // ! itemList as a ValueOrFunction is deprecated, the dataSource created from it is only temporary and should be removed soon.
    const getItems = (): DataSourceItem<T>[] => getVOFValue(propertyConfig.options?.itemList as ValueOrFunction<T[]>)?.map(i => { return { item: i }; }) ?? [];
    const getValues = (): T[] => getVOFValue(propertyConfig.options?.itemList as ValueOrFunction<T[]>) ?? [];
    const getItemFromValue = (value: string | number): DataSourceItem<T> | undefined => {
        return getItems().find(i => i.item && i.item.id === value);
    };
    const displayStringFunction = propertyConfig.options?.itemDisplayString;// defined as a const to avoid having options in dataSource.getDisplayString js context
    const dataSource: DataSource<T, string | number, unknown> = {
        getItems,
        getObjects: getValues,
        getItemFromValue,
        getValueFromItem: (item) => item.item?.id,
        getDisplayString: (value: string | number, itemFromCache?: DataSourceItem<T>) => {
            let item = itemFromCache;
            if (!item) {
                item = getItemFromValue(value);
            }
            if (displayStringFunction) {
                return displayStringFunction(value, item);
            }
            return item?.item?.name ?? "";
        }
    };
    return dataSource;
}

/**
 * Init ref list editor
 * @param propertyConfig 
 * @param context 
 * @returns 
 */
function initRefListEditor<T extends { id: string | number, name: string }>(propertyConfig: PropertyConfig<PropertyConfigType, PropertyOptionsList>, context?: anyReallyAny, options?: { ordered?: boolean }): CjsReferenceListEditor<DataSourceItem<T>, string | number> {

    const dataSource: DataSource<T, string | number, unknown> = initDataSource(<PropertyConfig<PropertyConfigType, PropertyOptionsList>>propertyConfig);

    const getItemValue = (item: DataSourceItem<T> | undefined): string | number | undefined => { return item ? dataSource.getValueFromItem(item) : undefined; };
    const getItemByValue = (items: DataSourceItem<T>[], id: string | number): DataSourceItem<T> | undefined => dataSource.getItemFromValue(id);
    const getItemDisplayString = (item: DataSourceItem<T>): string => {
        const value = getItemValue(item);
        return value != null ? dataSource.getDisplayString(value, item) : "";
    };
    const getData = (): DataSourceItem<T>[] => dataSource.getItems(context);

    const editor = new CjsReferenceListEditor<DataSourceItem<T>, string | number>({});
    editor.smartSelectOptions.setAndRefreshIfChanged({
        placeholderText: propertyConfig.options?.placeholder,
        callbacksToOverride: {
            getItemValue,
            getItemByValue
        },
        itemFormatter: (item) => {
            // We create div element for example, but here, the best way will be to only create the <span> element
            const itemDiv = document.createElement("div");
            itemDiv.innerHTML = "<span>" + getItemDisplayString(item) + " </span>";
            itemDiv.classList.add(SMART_SELECT_ITEM_ELEMENT, FLEX_CONTAINER_ROW, FLEX_ITEM_AUTO, H_100);
            itemDiv.style.alignItems = "center";
            itemDiv.style.paddingLeft = "5px";

            if (!editor.disabled) {
                itemDiv.style.cursor = "pointer";
            }

            itemDiv.setAttribute("value", String(getItemValue(item)));
            return itemDiv;
        },
        filterDisabled: propertyConfig.options?.filterDisabled,
        filterComparator: (p, renderCacheData) => {
            if (propertyConfig.options?.filterComparator) { return propertyConfig.options.filterComparator(p, renderCacheData); }
            else {
                return p.item != null && filterString(getItemDisplayString(p.item), p.filterValue);
            }
        }
    });

    // If ordered, enable dragAndDrop for table
    if (options?.ordered) {
        editor.selectedValuesTableOptions.setWithoutRefresh({
            dragAndDrop: {
                ...editor.selectedValuesTableOptions.dragAndDrop,
                enable: true
            }
        });
    }

    editor.setData(getData);
    return editor;
}


/**
 * Init ref table editor
 */
function initRefTableEditor<T extends { id: string | number, name: string }>(propertyConfig: PropertyConfig<PropertyConfigType, PropertyOptionsList>, context?: anyReallyAny): CjsReferenceTableEditor<DataSourceItem<T>, string | number> {

    const dataSource: DataSource<T, string | number, unknown> = initDataSource(<PropertyConfig<PropertyConfigType, PropertyOptionsList>>propertyConfig);

    const getItemValue = (item: DataSourceItem<T> | undefined): string | number | undefined => { return item ? dataSource.getValueFromItem(item) : undefined; };
    const getItemByValue = (items: DataSourceItem<T>[], id: string | number): DataSourceItem<T> | undefined => dataSource.getItemFromValue(id);
    const getItemDisplayString = (item: DataSourceItem<T>): string => {
        const value = getItemValue(item);
        return value != null ? dataSource.getDisplayString(value, item) : "";
    };
    const getData = (): DataSourceItem<T>[] => dataSource.getItems(context);

    const editor = new CjsReferenceTableEditor<DataSourceItem<T>, string | number>({
        options: {
            getItemValue,
            getItemByValue
        },
        smartTableOptions: {
            renderCell: cell => {
                const item = cell.rowItemData;
                if (item == null) { return ""; }

                // We create div element for example, but here, the best way will be to only create the <span> element
                const itemDiv = createHtmlElement("div", {
                    innerHTML: getItemDisplayString(item),
                    cssClass: [FLEX_CONTAINER_ROW, FLEX_ITEM_AUTO, H_100, ALIGN_ITEMS.CENTER, PADDING.PS_1]
                });

                if (!editor.disabled) {
                    itemDiv.style.cursor = "pointer";
                }

                itemDiv.setAttribute("value", String(getItemValue(item)));
                return itemDiv;
            }
        }
    });
    const contentColumn = editor.smartTableOptions.columns[1];
    if (contentColumn) {
        contentColumn.filterComparator = (p, renderCacheData) => {
            if (propertyConfig.options?.filterComparator) { return propertyConfig.options.filterComparator(p, renderCacheData); }
            else {
                return p.item != null && filterString(getItemDisplayString(p.item), p.filterValue);
            }
        };
    }
    editor.smartTable.setData({ data: getData() });

    return editor;
}

/**
 * Init a minimal tiny mce editor
 * @returns Minimal TinyMceEditor
 */
function initTinyMceEditor(): CjsTinyMceEditor {
    return new CjsTinyMceEditor({ configuration: "minimal" });
}

/**
 * Create an InputTextEditor with textarea enabled
 */
function initTextareaEditor(propertyConfig: PropertyConfig): CjsInputTextEditor {
    const editor = initTextEditor(propertyConfig);

    // Enable textarea and check pc options to set rows
    editor.inputOptions.setAndRefreshIfChanged({
        textarea: {
            enable: true
        }
    });
    return editor;
}

/**
 * Create a PhoneNumberEditor with appropriate propertyConfigs
 *
 * @param {PropertyConfig} propertyConfig
 */
function initPhoneNumberEditor(propertyConfig: PropertyConfig): CjsInputTextEditor {
    const editor = new CjsPhoneNumberEditor();
    // Set input options
    setInputWithButtonCommonOptions(editor, propertyConfig);
    return editor;
}

/**
 * Create a RangeEditor with appropriate propertyConfigs
 */
function initRangeEditor(propertyConfig: PropertyConfig<PropertyConfigType, PropertyOptionsRange>): CjsInputRangeEditor {
    const editor = new CjsInputRangeEditor();
    setInputWithButtonCommonOptions(editor, propertyConfig);
    if (propertyConfig.options?.displayValue) {
        editor.options.setWithoutRefresh({ displayValue: true });
    }
    if (propertyConfig.options?.max || propertyConfig.options?.min || propertyConfig.options?.step) {
        editor.inputOptions.setWithoutRefresh({ rangeOptions: propertyConfig.options });
    }
    return editor;
}


/**
 * Create a default initImageEditor
 */
function initImageEditor(propertyConfig: PropertyConfig): CjsImageEditor {
    const editor = new CjsImageEditor();
    return editor;
}

/** Set common options from propertyConfigs for editors who contains an inputWithButton */
export function setInputWithButtonCommonOptions(editor: CjsEditor<unknown>, propertyConfig: PropertyConfig, input?: CjsInputWithButton<string> | CjsInput): void {
    const editorInput = input ?? editor.getInput();
    if (editorInput instanceof CjsInputWithButton || editorInput instanceof CjsInput) {
        // Get options
        const options = editorInput instanceof CjsInputWithButton ? editorInput.inputOptions : editorInput.options;

        // set name
        options.setWithoutRefresh({ inputName: propertyConfig.typeUid + "_" + propertyConfig.propertyName });

        if (propertyConfig.options?.placeholder) {
            options.setWithoutRefresh({ placeholderText: propertyConfig.options?.placeholder });
        }
        if (propertyConfig.options?.prefix) {
            options.setWithoutRefresh({ inputPrefixText: propertyConfig.options.prefix });
        }
        if (propertyConfig.options?.suffix) {
            options.setWithoutRefresh({ inputSuffixText: propertyConfig.options.suffix });
        }
        if (propertyConfig.options?.clearButton) {
            options.setWithoutRefresh({ enableClearButton: true });
        }
        if ((propertyConfig.options as PropertyOptionsTextArea)?.rows && editorInput instanceof CjsInput) {
            editorInput.components.inputTextarea.setAttribute("rows", String((propertyConfig.options as PropertyOptionsTextArea).rows));
        }

    }

}

